// This is a part of the Cadcorp SIS source code. 
// Copyright (c) Cadcorp Limited 1991-2026 
import { IAjax, ISchemaColumn, IItem, IGeognoSISItem } from '../../../../CommonScripts/Abstractions';
/**
* Handles queries on individual items.
*/
export class Item implements IGeognoSISItem{

    /**
    * @property {object} id - Item id
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    id: any;
    /**
    * @property {number} layerIndex - The index of the layer this item is on
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    layerIndex: number;
    /**
    * @property {string} sessionId - GeognoSIS Session ID
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    sessionId: string;
    /**
    * @property {string} mapName - Current map name
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    mapName: string;
    /**
    * @property {object} geometry - Feature geometry
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    geometry: any;
    /**
    * @property {Object} geoJSON - GeoJSON for the item.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    geoJSON: any;
    /**
    * @property {Object} properties - Item properties.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @public
    */
    properties: any;
    /**
     * @property {string|null} baseUrl The base url for the application
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
     * @public
     * @readonly
     */
    readonly baseUrl: string;
    /**
     * @property {string} layerId The unique id for the swd this item belongs to
     * @public
     */
    readonly layerId: string;
    /**
     * @property {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @public
     */
    readonly ajaxWrapper: IAjax;
    /**
    * @property {Object} m_lastUpdateProperties - The set of properties from the last successful update.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @private
    */
    private m_lastUpdateProperties: any;
    /**
    * @property {Object} m_lastUpdateGeoJSON - GeoJSON from the last successful update.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @private
    */
    private m_lastUpdateGeoJSON :any;

    /**
     * Constructor.
     * @param {Object} config - configuration options.
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
     * @public 
     */
    constructor(config: any) {
        this.properties = config.feature.properties;
        this.layerIndex = config.layerIndex;
        this.sessionId = config.sessionId;
        this.id = config.feature.id;
        this.mapName = config.mapName;
        this.geometry = config.feature.geometry;
        this.baseUrl = config.baseUrl;
        this.geoJSON = null;
    }
    /**
    * Update the geometry of this item
    * @public
    * @param {string} newGeoJson - The string representation of the new geogjson 
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    */
    updateGeometry(newGeoJson: string): Promise<IGeognoSISItem> {
        this.geoJSON = newGeoJson;
        var url = this.getUpdateGeometryUrl();
        return this.ajaxWrapper.patch(url, this.geoJSON).then((response) => {
             return this.geoJSONUpdatedSuccessHandler(response);
            }
        );
    }
    /**
     * Gets the url to update the geometry
     * @protected 
     * @returns {string} The url
     */
    protected getUpdateGeometryUrl(): string {
        return `${this.baseUrl}/${this.mapName}/${this.layerId}/Search/GeognoSis/${this.sessionId}/${this.layerIndex}/${this.id}`;
    }
    /**
    * Gets the url to update the properties
    * @protected
    * @returns {string} The url
    */
    protected getUpdatePropetiesUrl(): string {
        return `${this.baseUrl}/${this.mapName}/${this.layerId}/Search/GeognoSis/${this.sessionId}/${this.layerIndex}/${this.id}/Properties`;
    }
    /**
    * Gets the url to delete the item
    * @protected
    * @returns {string} The url
    */
    protected getDeleteUrl(): string {
        return `${this.baseUrl}/${this.mapName}/${this.layerId}/Search/GeognoSis/${this.sessionId}/${this.layerIndex}/${this.id}`;
    }

    /**
    * Update the properties of this item
    * @public
    * @param {object} newValues - The new properties to set on this item 
    * @param {Array.<Cadcorp.Web.Entities.GeognoSIS.OverlaySchema>} schemaColumns - The schema for this layer 
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    */
    updateProperties(newValues: any, schemaColumns: ISchemaColumn[]): Promise<IGeognoSISItem>{
        //{"PropertyMap": { "Description$":"new value" }}
        //{"PropertyMap": { "ADate@":{"date":"yyyy-MM-ddTHH:mm:ss.ssZ" }}}
        var updateObj = {};
        var updateRequired = false;
        this.m_lastUpdateProperties = this.cloneProperties(this.properties);
        //Check to see if there are any updates
        for (var c = 0; c < schemaColumns.length; c++) {
            var schemaCol = schemaColumns[c];
            var newPropVal = null;
            var oldValue = null;
            if (newValues[schemaCol.name])
                newPropVal = newValues[schemaCol.name];
            if (schemaCol.type === 11 || schemaCol.type === 8)
                oldValue = this.properties[schemaCol.name];
            else {
                if (this.properties[schemaCol.name]) {
                    oldValue = this.properties[schemaCol.name][schemaCol.typeName];
                }
            }

            if (newPropVal !== null && newPropVal !== oldValue) {

                if (schemaCol.type === 11 || schemaCol.type === 8) {
                    updateObj[schemaCol.expression] = newPropVal;
                    this.properties[schemaCol.name] = newPropVal;
                }
                else if (schemaCol.type === 7) {//dates require special treatment
                    var dateObj = {};
                    dateObj[schemaCol.typeName] = newPropVal;
                    this.properties[schemaCol.name] = dateObj;
                    updateObj[schemaCol.expression] = dateObj;
                }
                else {
                    updateObj[schemaCol.expression] = newPropVal;
                    if (this.properties[schemaCol.name]) {
                        this.properties[schemaCol.name][schemaCol.typeName] = newPropVal;
                    }
                    else {
                        var objVal = {};
                        objVal[schemaCol.typeName] = newPropVal;
                        this.properties[schemaCol.name] = objVal;
                    }
                }
                updateRequired = true;
            }
        }
        var retVal;
        if (updateRequired) {
            var updateJson = {"PropertyMap":' + JSON.stringify(updateObj) + '};
            var url = this.getUpdatePropetiesUrl();
            retVal = this.ajaxWrapper.patch(url, updateJson).then((response) => {
                    return this.propertiesUpdatedSuccessHandler(response);
                }
            );
        }
        else {
            retVal = Promise.resolve(this);
        }
        return retVal;
    }
    /**
    * Perform a shallow clone of the supplied object
    * @private
    * @param {object} objectToClone - The object to clone 
    * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
    * @returns {Object} The newly created object
    */
    protected cloneProperties(objectToClone) {
        var retVal = {};
        for (var prop in objectToClone) {
            if (objectToClone.hasOwnProperty(prop)) {
                var property = objectToClone[prop];
                if (typeof property === 'object') {
                    retVal[prop] = this.cloneProperties(property);
                }
                else {
                    retVal[prop] = objectToClone[prop];
                }
            }
        }
        return retVal;
    }
    /**
     * Success Handler for the update properties.
     * @protected
     * @param {Object} eventArgs - item properties data object.
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
     */
    protected propertiesUpdatedSuccessHandler(eventArgs) {
        this.m_lastUpdateProperties = this.cloneProperties(this.properties);
        return this;
    }
    /**
     * Success Handler for the update geometry.
     * @protected
     * @param {Object} eventArgs - item properties data object.
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
     */
    protected geoJSONUpdatedSuccessHandler(eventArgs) : Item {
        this.m_lastUpdateGeoJSON = this.geoJSON;
        return this;
    }
    /**
     * Delete this item
     * @public
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
     */
    deleteItem(): Promise<IGeognoSISItem> {
        var url = this.getDeleteUrl();

        return this.ajaxWrapper.delete(url);
    }
    /**
     * @public
     * Destroys this object.
     * @memberof Cadcorp.Web.Entities.GeognoSIS.Item#
     */
    destroy() {
        this.properties = null;
        this.layerIndex = null;
        this.sessionId = null;
        this.id = null;
        this.mapName = null;
        this.geometry = null;
    }
};
/**
 * Extends Cadcorp.Web.Entities.GeognoSIS.CursorItem
 * @class 
 * @public
 * @public
 * @extends Cadcorp.Web.Entities.GeognoSIS.CursorItem#
 */
export class CursorItem extends Item {
    /**
    * @property {string} m_cursorId - cursor id.
    * @private
    */
    private m_cursorId: string;
    /**
     * Constructor for Cadcorp based classes.
     * @param {Object} config - configuration object.
     * @memberof Cadcorp.Web.Entities.GeognoSISCursorItem#
     * @private
     * @function
     */
    constructor(config: any) {
        super(config);
        this.m_cursorId = config.cursorId;
    }
    /**
    *@inheritdoc 
    */
    protected getUpdateGeometryUrl(): string {
        return `${this.baseUrl}/${this.mapName}/${this.layerId}/Search/GeognoSis/${this.sessionId}/${this.layerIndex}.${this.id}`;
    }
    /**
    *@inheritdoc
    */
    protected getUpdatePropetiesUrl(): string {
        return `${this.baseUrl}/${this.mapName}/${this.layerId}/Search/GeognoSis/${this.sessionId}/${this.layerIndex}.${this.id}/Properties`;
    }
    /**
    *@inheritdoc
    */
    protected getDeleteUrl(): string {
        return `${this.baseUrl}/${this.mapName}/${this.layerId}/Search/GeognoSis/${this.sessionId}/${this.layerIndex}.${this.id}`;
    }
};

